import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface OnboardingData {
  id: string;
  tenantId: string;
  locationId: string | null;
  businessType: 'company' | 'individual' | 'non_profit' | 'warehouse' | null;
  legalBusinessName: string | null;
  ein: string | null;
  industry: string | null;
  otherIndustry: string | null;
  businessWebsite: string | null;
  businessPhone: string | null;
  dbaName: string | null;
  businessStartedDate: string | null;
  averageMonthlySalesVolume: string | null;
  averageSaleAmountPerTransaction: string | null;
  businessStreet: string | null;
  businessCity: string | null;
  businessState: string | null;
  businessZip: string | null;
  businessCountry: string | null;
  repFirstName: string | null;
  repLastName: string | null;
  repDob: string | null;
  repSsnLast4: string | null;
  repEmail: string | null;
  repPhone: string | null;
  repJobTitle: string | null;
  ownershipPercentage: string | null;
  repStreet: string | null;
  repCity: string | null;
  repState: string | null;
  repZip: string | null;
  repCountry: string | null;
  repDriverLicenseNumber: string | null;
  repDriverLicenseState: string | null;
  repDriverLicenseExpiry: string | null;
  routingNumber: string | null;
  accountNumber: string | null;
  bankName: string | null;
  bankRepresentativeName: string | null;
  currentStep: string;
  isSubmitted: boolean;
  submittedAt: string | null;
}

function buildQueryString(locationId?: string) {
  return locationId ? `?locationId=${locationId}` : '';
}

export function useOnboarding(locationId?: string) {
  return useQuery({
    queryKey: ['onboarding', locationId ?? null],
    queryFn: () => api.get<{ data: OnboardingData | null }>(`/onboarding${buildQueryString(locationId)}`),
  });
}

export function useSaveOnboarding(locationId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<OnboardingData>) =>
      api.put<{ data: OnboardingData }>(`/onboarding${buildQueryString(locationId)}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', locationId ?? null] });
    },
  });
}

export function useSubmitOnboarding(locationId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ data: OnboardingData }>(`/onboarding/submit${buildQueryString(locationId)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });
}
